import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule, HttpErrorResponse, HttpEventType, HttpParams, HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatRippleModule, ThemePalette } from '@angular/material/core';
import { MatProgressBarModule, ProgressBarMode } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { NgxFileDropEntry, FileSystemFileEntry, NgxFileDropModule } from 'ngx-file-drop';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface FileEntry extends NgxFileDropEntry {
    progress?: number;
    totalSize?: string;
    uploadedSize?: string;
    status?: string;
    key?: string;
    url?: string
}

enum FileStatusEnum {
    NOT_STARTED = "NOT_STARTED",
    UPLOADED = "UPLOADED",
    ERROR = "ERROR"
}

const Material = [
    MatInputModule,
    MatFormFieldModule,
    MatRippleModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule
];

@Component({
    selector: 'eq-fileInput',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        HttpClientModule,
        NgxFileDropModule,
        MatCardModule,
        MatRadioModule,
        MatProgressBarModule,
        ...Material
    ],
    templateUrl: './file-input.component.html',
    styleUrl: './file-input.component.scss'
})
export class FileInputComponent {
    @Output() filesUploaded: EventEmitter<{ key: string, location: string, url: string }[]> = new EventEmitter<{ key: string, location: string, url: string }[]>();

    @Input() disabled: boolean = false;
    @Input() multiple: boolean = true;
    @Input() accept: string = "*/*";
    @Input() supportedFormats: string = "JPEG, PNG, PDF";
    @Input() header: string = "";
    @Input() submitButtonText: string = "UPLOAD FILES";
    @Input() path: string = "/";
    @Input() isUploadHeaderRequired: boolean = true;
    @Input() tip: string = "";
    @Input() cancelButtonText: string = '';
    @Input() customHeader: string = '';
    @Output() backEvent = new EventEmitter<any>();

    files: FileEntry[] = [];
    uploadedFileCount: number = 0;
    uploadedFiles: { key: string, location: string, url: string, contentType: string }[] = [];
    color: ThemePalette = 'accent';
    mode: ProgressBarMode = 'determinate';
    bufferValue: number = 100;
    isFileUploadingStart: boolean = false;

    constructor(
        private http: HttpClient,
        @Inject('environment') private environment: any,
        private readonly matSnackBar: MatSnackBar
    ) { }
    dropped(files: NgxFileDropEntry[]): void {
        this.files = [...this.files, ...files];
        for (const droppedFile of this.files) {

            if (droppedFile.fileEntry.isFile && !droppedFile.status) {
                
                const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
                fileEntry.file((file: File) => {
                    if(this.getFileType(file) == "heic"){
                        this.showError("HEIC files are not supported. Please upload a JPEG, JPG, or PNG instead.")
                        this.files = []
                        return;
                    }                
                    droppedFile.totalSize = this.formatFileSize(file.size);
                    droppedFile.uploadedSize = "0";
                    droppedFile.status = FileStatusEnum.NOT_STARTED;
                    droppedFile.progress = 0;
                })
            }
        }
    }

    upload(): void {
        this.isFileUploadingStart = true;
        for (const droppedFile of this.files) {
            if (droppedFile.progress === 100) continue;
            else if (droppedFile.fileEntry.isFile) {
                const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
                fileEntry.file((file: File) => {
                    const formData: FormData = new FormData();
                    formData.append('file', file, droppedFile.relativePath);
                    formData.append('path', this.path);
                    this.http.post(`${this.environment.apiUrl}/file/upload`, formData, {
                        reportProgress: true,
                        observe: 'events'
                    }).subscribe({
                        next: (event: any) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                droppedFile.uploadedSize = this.formatFileSize(event.loaded);
                                droppedFile.progress = Math.round(100 * event.loaded / event.total);
                            } else if (event instanceof HttpResponse) {
                                droppedFile.key = event.body.key;
                                droppedFile.url = event.body.url;
                                this.uploadedFiles.push({
                                    key: event.body.key,
                                    location: event.body.location,
                                    url: event.body.url,
                                    contentType: event.body.contentType
                                });
                                droppedFile.status = FileStatusEnum.UPLOADED;
                                this.uploadedFileCount++;

                                if (this.uploadedFileCount === this.files.length) {
                                    this.filesUploaded.emit(this.uploadedFiles);
                                }
                            }
                        },
                        error: (err: HttpErrorResponse) => {
                            console.log(err)
                            droppedFile.progress = 0;
                            droppedFile.status = FileStatusEnum.ERROR;
                        }
                    });
                })
            }
        }
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) {
            return bytes + ' B';
        } else if (bytes < 1024 * 1024) {
            return Math.floor(bytes / 1024) + ' KB';
        } else if (bytes < 1024 * 1024 * 1024) {
            return Math.floor(bytes / (1024 * 1024)) + ' MB';
        } else {
            return Math.floor(bytes / (1024 * 1024 * 1024)) + ' GB';
        }
    }

    downloadFile(url: any): void {
        const startIndex = url.lastIndexOf("/") + 1;
        const endIndex = url.indexOf("?");
        const imgName = url.substring(startIndex, endIndex !== -1 ? endIndex : undefined);
        this.http
            .get(url, { responseType: "blob" as "json" })
            .subscribe((res: any) => {
                const file = new Blob([res], { type: res.type });

                const blob = window.URL.createObjectURL(file);
                const link = document.createElement("a");
                link.href = blob;
                link.download = imgName;

                link.dispatchEvent(
                    new MouseEvent("click", {
                        bubbles: true,
                        cancelable: true,
                        view: window
                    })
                );

                setTimeout(() => {
                    window.URL.revokeObjectURL(blob);
                    link.remove();
                }, 100);
            });
    }

    delete(fileIndex: number): void {
        const fileData = this.files.find((data: FileEntry, index: number) => index === fileIndex);
        if (fileData) {
            if (fileData.status === FileStatusEnum.UPLOADED && fileData.key) {
                const params = new HttpParams().set('key', fileData.key);
                this.http.delete<{ success: boolean }>(`${this.environment.apiUrl}/file`, { params }).subscribe({
                    next: (data: { success: boolean }) => {
                        if (data.success) {
                            this.files.splice(fileIndex, 1);
                            this.uploadedFiles.splice(fileIndex, 1);
                            this.uploadedFileCount--;
                            this.filesUploaded.emit(this.uploadedFiles);
                        }
                    },
                    error: (error: HttpErrorResponse) => {
                        console.log(error);
                    }
                })
            } else {
                this.files.splice(fileIndex, 1);
                this.uploadedFileCount--;
            }
        }
    }

    goBack() {
        this.backEvent.emit();
    }
    getFileType(file: File){
        const t = file.name.split(".");
        return t[t.length - 1].toLocaleLowerCase();
    }  

    showError(message: string){
    this.matSnackBar.open(
          message,
          'Dismiss',
          {
            duration: 3000,
            panelClass: ['error-snackbar'],
          }
        );
    }
}